/**
 * MembershipDialog — Mandanten-Zuordnungen eines Users bearbeiten.
 *
 * Iter88m (2026-02-19): Ersetzt das Curl-Wackelwerkzeug durch eine echte UI.
 * Als Super-Admin kannst du für jeden User:
 *   • bestehende Memberships listen (Novatus + DSS + …)
 *   • pro Zeile Rolle, Marge, Primary-Flag ändern
 *   • Zuordnung entfernen
 *   • neue Zuordnung zu einem anderen Mandanten anlegen
 */
import React, { useEffect, useState } from "react";
import { X, Save, Trash2, Plus, Star, Users, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const ROLE_OPTIONS = [
  { value: "admin", label: "Administrator" },
  { value: "sales_director", label: "Vertriebsdirektor" },
  { value: "sales_manager", label: "Vertriebsleiter" },
  { value: "branch_manager", label: "Niederlassungsleiter" },
  { value: "sales_rep", label: "Vertriebler" },
  { value: "telesales_director", label: "Telesales-Direktor" },
  { value: "agent", label: "Telesales-Agent" },
  { value: "installer", label: "Installateur" },
];

export default function MembershipDialog({ userId, onClose }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [user, setUser] = useState(null);
  const [memberships, setMemberships] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [addDraft, setAddDraft] = useState({ tenant_id: "", role: "sales_rep", margin_value: "", is_primary: false });

  const load = async () => {
    setLoading(true);
    try {
      const [msRes, tRes] = await Promise.all([
        api.get(`/superadmin/users/${userId}/memberships`),
        api.get(`/superadmin/tenants`),
      ]);
      setUser(msRes.data.user);
      setMemberships(msRes.data.memberships || []);
      setTenants(tRes.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Laden fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (userId) load(); }, [userId]);

  const updateLocal = (tenantId, patch) => {
    setMemberships((prev) => prev.map((m) => m.tenant_id === tenantId ? { ...m, ...patch, _dirty: true } : m));
  };

  const saveRow = async (m) => {
    setSaving(true);
    try {
      await api.patch(`/superadmin/users/${userId}/memberships/${m.tenant_id}`, {
        role: m.role,
        margin_value: m.margin_value === "" ? null : Number(m.margin_value),
        is_primary: !!m.is_primary,
      });
      toast.success("Zuordnung gespeichert");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const removeRow = async (m) => {
    if (!window.confirm(`Zuordnung zu "${m.tenant_name}" wirklich entfernen?`)) return;
    setSaving(true);
    try {
      await api.delete(`/superadmin/users/${userId}/memberships/${m.tenant_id}`);
      toast.success("Zuordnung entfernt");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Entfernen fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const addMembership = async () => {
    if (!addDraft.tenant_id) { toast.error("Bitte Mandanten wählen"); return; }
    setSaving(true);
    try {
      await api.post(`/superadmin/users/${userId}/memberships`, {
        tenant_id: addDraft.tenant_id,
        role: addDraft.role,
        margin_value: addDraft.margin_value === "" ? null : Number(addDraft.margin_value),
        is_primary: !!addDraft.is_primary,
      });
      toast.success("Zuordnung hinzugefügt");
      setAddOpen(false);
      setAddDraft({ tenant_id: "", role: "sales_rep", margin_value: "", is_primary: false });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Anlegen fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const usedTenantIds = new Set(memberships.map((m) => m.tenant_id));
  const availableTenants = tenants.filter((t) => !usedTenantIds.has(t.id));

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="membership-dialog">
      <div className="bg-[#0B0B0D] border border-zinc-800 rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-zinc-800 bg-gradient-to-r from-emerald-500/10 to-transparent">
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-lg bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
              <Users size={16} className="text-emerald-300" />
            </div>
            <div>
              <div className="text-[12px] uppercase tracking-widest text-emerald-300 font-semibold">Mandanten-Zuordnungen</div>
              <div className="text-[14.5px] font-bold text-zinc-100">
                {user ? `${user.first_name || ""} ${user.last_name || ""}`.trim() || user.email : "…"}
              </div>
            </div>
          </div>
          <button onClick={onClose} data-testid="membership-close" className="h-8 w-8 rounded-lg bg-zinc-800 hover:bg-zinc-700 flex items-center justify-center">
            <X size={14} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-zinc-400"><Loader2 size={20} className="animate-spin mr-2" /> Lade Zuordnungen…</div>
          ) : (
            <>
              {memberships.length === 0 && (
                <div className="text-sm text-zinc-400 italic bg-zinc-950 border border-zinc-800 rounded-lg p-4">
                  Keine Mandanten-Zuordnungen. Füge unten die erste hinzu.
                </div>
              )}
              {memberships.map((m) => (
                <div key={m.tenant_id} className="bg-zinc-950 border border-zinc-800 rounded-lg p-3" data-testid={`membership-row-${m.tenant_id}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <div className="font-bold text-zinc-100 flex-1">
                      {m.tenant_name || m.tenant_id}
                      {m.is_native && <span className="ml-2 text-[10px] uppercase tracking-widest bg-sky-500/15 border border-sky-500/40 text-sky-300 px-1.5 py-0.5 rounded font-bold">Heim-Tenant</span>}
                      {m.is_primary && <span className="ml-2 text-[10px] uppercase tracking-widest bg-amber-500/15 border border-amber-500/40 text-amber-300 px-1.5 py-0.5 rounded font-bold inline-flex items-center gap-1"><Star size={10} /> Primary</span>}
                    </div>
                    <button onClick={() => removeRow(m)} disabled={saving} data-testid={`membership-delete-${m.tenant_id}`}
                      className="h-8 px-2.5 rounded-lg bg-rose-500/10 border border-rose-500/40 text-rose-300 hover:bg-rose-500/20 inline-flex items-center gap-1.5 text-[11px] font-semibold">
                      <Trash2 size={12} /> Entfernen
                    </button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <label className="text-[11px] uppercase tracking-widest text-zinc-400 flex flex-col gap-1">
                      Rolle
                      <select value={m.role || "sales_rep"} onChange={(e) => updateLocal(m.tenant_id, { role: e.target.value })}
                        data-testid={`membership-role-${m.tenant_id}`}
                        className="h-9 bg-[#09090B] border border-zinc-700 rounded-md px-2 text-[13px] text-zinc-100 focus:border-emerald-500 outline-none">
                        {ROLE_OPTIONS.map((r) => (<option key={r.value} value={r.value}>{r.label}</option>))}
                      </select>
                    </label>
                    <label className="text-[11px] uppercase tracking-widest text-zinc-400 flex flex-col gap-1">
                      Marge (%)
                      <input type="number" step="0.5" value={m.margin_value ?? ""} onChange={(e) => updateLocal(m.tenant_id, { margin_value: e.target.value })}
                        data-testid={`membership-margin-${m.tenant_id}`}
                        className="h-9 bg-[#09090B] border border-zinc-700 rounded-md px-2 text-[13px] text-zinc-100 focus:border-emerald-500 outline-none" />
                    </label>
                    <label className="text-[11px] uppercase tracking-widest text-zinc-400 flex items-end gap-2 pb-1">
                      <input type="checkbox" checked={!!m.is_primary} onChange={(e) => updateLocal(m.tenant_id, { is_primary: e.target.checked })}
                        data-testid={`membership-primary-${m.tenant_id}`}
                        className="h-4 w-4 rounded border-zinc-600 bg-[#09090B] text-emerald-500 focus:ring-emerald-500" />
                      <span className="text-[12px] normal-case text-zinc-200 font-semibold">Als Primary setzen</span>
                    </label>
                  </div>
                  {m._dirty && (
                    <div className="mt-2 flex justify-end">
                      <button onClick={() => saveRow(m)} disabled={saving} data-testid={`membership-save-${m.tenant_id}`}
                        className="h-8 px-3 rounded-lg bg-emerald-500/20 border border-emerald-500/50 text-emerald-200 hover:bg-emerald-500/30 inline-flex items-center gap-1.5 text-[12px] font-bold">
                        <Save size={12} /> Speichern
                      </button>
                    </div>
                  )}
                </div>
              ))}

              {addOpen ? (
                <div className="bg-zinc-950 border-2 border-emerald-500/40 rounded-lg p-3" data-testid="membership-add-form">
                  <div className="text-[12px] uppercase tracking-widest text-emerald-300 font-bold mb-2">Neue Zuordnung</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
                    <label className="text-[11px] uppercase tracking-widest text-zinc-400 flex flex-col gap-1">
                      Mandant
                      <select value={addDraft.tenant_id} onChange={(e) => setAddDraft({ ...addDraft, tenant_id: e.target.value })}
                        data-testid="membership-add-tenant"
                        className="h-9 bg-[#09090B] border border-zinc-700 rounded-md px-2 text-[13px] text-zinc-100 focus:border-emerald-500 outline-none">
                        <option value="">— Mandant wählen —</option>
                        {availableTenants.map((t) => (<option key={t.id} value={t.id}>{t.name}</option>))}
                      </select>
                    </label>
                    <label className="text-[11px] uppercase tracking-widest text-zinc-400 flex flex-col gap-1">
                      Rolle
                      <select value={addDraft.role} onChange={(e) => setAddDraft({ ...addDraft, role: e.target.value })}
                        data-testid="membership-add-role"
                        className="h-9 bg-[#09090B] border border-zinc-700 rounded-md px-2 text-[13px] text-zinc-100 focus:border-emerald-500 outline-none">
                        {ROLE_OPTIONS.map((r) => (<option key={r.value} value={r.value}>{r.label}</option>))}
                      </select>
                    </label>
                    <label className="text-[11px] uppercase tracking-widest text-zinc-400 flex flex-col gap-1">
                      Marge (%)
                      <input type="number" step="0.5" value={addDraft.margin_value} onChange={(e) => setAddDraft({ ...addDraft, margin_value: e.target.value })}
                        data-testid="membership-add-margin"
                        className="h-9 bg-[#09090B] border border-zinc-700 rounded-md px-2 text-[13px] text-zinc-100 focus:border-emerald-500 outline-none" />
                    </label>
                    <label className="text-[11px] uppercase tracking-widest text-zinc-400 flex items-end gap-2 pb-1">
                      <input type="checkbox" checked={addDraft.is_primary} onChange={(e) => setAddDraft({ ...addDraft, is_primary: e.target.checked })}
                        data-testid="membership-add-primary"
                        className="h-4 w-4 rounded border-zinc-600 bg-[#09090B] text-emerald-500 focus:ring-emerald-500" />
                      <span className="text-[12px] normal-case text-zinc-200 font-semibold">Als Primary setzen</span>
                    </label>
                  </div>
                  <div className="flex justify-end gap-2">
                    <button onClick={() => setAddOpen(false)} disabled={saving}
                      className="h-8 px-3 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-[12px] font-semibold">Abbrechen</button>
                    <button onClick={addMembership} disabled={saving || !addDraft.tenant_id} data-testid="membership-add-save"
                      className="h-8 px-3 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-black inline-flex items-center gap-1.5 text-[12px] font-bold">
                      <Plus size={12} /> Anlegen
                    </button>
                  </div>
                </div>
              ) : (
                availableTenants.length > 0 && (
                  <button onClick={() => setAddOpen(true)} data-testid="membership-add-open"
                    className="w-full h-10 rounded-lg border-2 border-dashed border-zinc-700 hover:border-emerald-500/50 text-zinc-300 hover:text-emerald-300 inline-flex items-center justify-center gap-2 text-[13px] font-semibold">
                    <Plus size={14} /> Mandant hinzufügen
                  </button>
                )
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
